import {resourceEnumerateInstance} from './resource-enumerate-instance';
import {
  apiFormatters,
  apiNamespaces,
  cacheNamespaces,
  exceptionCodes,
  licenseTransferTypes,
  localStorageKeys,
  pages,
  defaultLocale,
} from './constants-enumerate';
import BasicApiFormatter from '@modules/basic-api-formatter';
import FileUploadApiFormatter from '@modules/file-upload-api-formatter';
import DownloadApiFormatter from '@modules/download-api-formatter';
import {ApiRequest} from '@aofl/api-request';
import {routerInstance} from '@aofl/router';
import {Toast} from './toast';
import {toastStatus} from '@modules/i18n-labels';
import {wrapUnsafeHtml} from '@modules/wrap-unsafe-html';
import {ApiService as ComponentsApiService} from '@epg/epg-components';

const apiRequestInstance = new ApiRequest();
apiRequestInstance.addFormatter(apiFormatters.BASIC, BasicApiFormatter);
apiRequestInstance.addFormatter(apiFormatters.UPLOAD, FileUploadApiFormatter);
apiRequestInstance.addFormatter(apiFormatters.DOWNLOAD, DownloadApiFormatter);

/**
 *
 */
class ApiService {
  /**
   * @type {string | null}
   */
  static tallyTestId = localStorage.getItem('tally_test_id') || '';

  /**
   * @param {String} apiEndpointKey
   * @param {Object} payload
   * @param {String} format
   * @param {Boolean} fromCache
   * @param {String} namespace
   *
   * @return {Promise}
   */
  static async request(apiEndpointKey, payload, format, fromCache, namespace) {
    try {
      await resourceEnumerateInstance.ready;
      const reData = await resourceEnumerateInstance.get(apiNamespaces.EMS);
      const url = reData.payload.api_domains.api_root_url + reData.payload.api_endpoints[apiEndpointKey];
      return await apiRequestInstance.request(url, payload, format, fromCache, namespace);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
      const code = e?.payload?.error?.code || e?.error?.code;
      if (
        code === exceptionCodes.MAINTENANCE &&
        window.location.pathname.includes('/503') === false
      ) {
        window.stop();
        window.location = pages.ERROR_503;
      } else if (
        code === exceptionCodes.LOCKED &&
        apiEndpointKey !== 'user_validate' &&
        routerInstance.currentRoute.matchedRoute.path !== pages.BASE
      ) {
        window.stop();
        ApiService.logout();
        Toast.error(wrapUnsafeHtml(toastStatus.orgLocked), '');
      } else if (
        apiEndpointKey !== 'user_validate' &&
        code === exceptionCodes.AUTHENTICATION_INVALID_TOKEN &&
        routerInstance.currentRoute.matchedRoute.path !== pages.BASE
      ) {
        window.stop();
        window.location = pages.HOME;
        return;
      }

      throw e;
    }
  }

  static async getDownloadRequestURL(apiEndpointKey) {
    await resourceEnumerateInstance.ready;
    const reData = await resourceEnumerateInstance.get(apiNamespaces.EMS);
    return reData.payload.api_domains.download_url + reData.payload.api_endpoints[apiEndpointKey];
  }

  /**
   * Returns a download url for the given file key
   *
   * @param {String} key
   *
   * @return {Promise}
   */
  static async getDownloadFileUrl(key) {
    await resourceEnumerateInstance.ready;
    const reData = await resourceEnumerateInstance.get(apiNamespaces.EMS);

    return reData.payload.api_domains.file_root_url +
      '?key=' +
      encodeURIComponent(key);
  }

  /**
   * Returns a download url for the given file key
   *
   * @param {String} key
   * @param {String} groupPath
   *
   * @return {Promise}
   */
  static async getDownloadReportUrl(key, groupPath) {
    await resourceEnumerateInstance.ready;
    const reData = await resourceEnumerateInstance.get(apiNamespaces.EMS);

    return reData.payload.api_domains.report_download_url +
      '?key=' +
      encodeURIComponent(key) +
      '&group_path=' +
      encodeURIComponent(groupPath);
  }

  /**
   * Returns a download url for the given file key
   *
   * @param {String} jobId
   *
   * @return {Promise}
   */
  static async getDownloadDashboardCSVUrl(jobId) {
    await resourceEnumerateInstance.ready;
    const reData = await resourceEnumerateInstance.get(apiNamespaces.EMS);

    return reData.payload.api_domains.dashboard_csv_download_url +
      '?job_id=' +
      encodeURIComponent(jobId);
  }

  static statsGetActivity(parentId, product, searchParams, start, end) {
    return ApiService.request(
      'stats_get_activity',
      {
        parent_id: parentId,
        product,
        search: searchParams?.search,
        sort: searchParams?.sort,
        filters: searchParams?.filters,
        start,
        end,
        test_id: this.tallyTestId
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }

  static statsGetUsage(parentId, product, searchParams, start, end) {
    return ApiService.request(
      'stats_get_usage',
      {
        parent_id: parentId,
        product,
        search: searchParams?.search,
        sort: searchParams?.sort,
        filters: searchParams?.filters,
        start,
        end,
        test_id: this.tallyTestId
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }

  static statsGroupDashboard(groupId, products, end_timestamp = Math.floor(Date.now() / 1000)) {
    return ApiService.request(
      'stats_group_dashboard',
      {group_id: groupId, products, test_id: this.tallyTestId, end_timestamp},
      apiFormatters.BASIC,
      true,
      cacheNamespaces.MANUAL_ROSTER_API);
  }

  static dashboardCreateCSVExport(groupId, product, type, startEnd = null, metadata = {}) {
    return ApiService.request(
      'dashboard_create_csv_export',
      {
        group_id: groupId,
        product,
        type,
        start_end: startEnd,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
        meta_data: metadata,
      },
      apiFormatters.BASIC,
      false
    );
  }

  /**
   * Calls User/Login to login user with provided credentials
   *
   * @param {String} email
   * @param {String} password
   * @param {String} mfaMethod
   * @param {String} mfaToken
   * @param {Boolean} mfaRemember
   * @return {Promise}
   */
  static login(email, password, mfaRemember = false, mfaMethod = '', mfaToken = '') {
    const payload = {
      email,
      password,
      mfa_remember: mfaRemember,
      mfa_method: mfaMethod,
      mfa_token: mfaToken,
    };
    apiRequestInstance.purgeCache();

    return ApiService.request('user_login', payload, apiFormatters.BASIC, false, cacheNamespaces.AUTH_API);
  }

  /**
   * Calls Third Party SSO Endpoint
   *
   * @param {string} provider
   * @param {string} code
   * @return {Promise}
   */
  static ssoLogin(provider, code) {
    let endpoint = '';
    if (provider === 'clever') {
      endpoint = 'oauth_clever';
    } else if (provider === 'classlink') {
      endpoint = 'oauth_classlink';
    } else {
      window.stop();
      window.location = pages.HOME;
    }
    const payload = {
      code,
      current_url: window.location.href,
    };
    apiRequestInstance.purgeCache();

    return ApiService.request(endpoint, payload, apiFormatters.BASIC, false, 'test');
  }

  /**
   * Calls User/Login to login user with provided credentials
   *
   * @param {String} username
   * @param {String} password
   * @return {Promise}
   */
  static userCreatePasswordResetToken(username, password) {
    const payload = {
      username,
      password
    };
    return ApiService.request('user_create_password_reset_token', payload, apiFormatters.BASIC, false, cacheNamespaces.AUTH_API);
  }

  /**
   * Calls User/LoginByToken to login user with provided token
   *
   * @param {String} token
   * @return {Promise}
   */
  static loginByToken(token) {
    const payload = {token};
    apiRequestInstance.purgeCache();

    return ApiService.request(
      'user_login_by_token',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.AUTH_API
    );
  }

  /**
   * Calls User/userValidate to check to see if user has a valid session.
   *
   * @return {Promise}
   */
  static userValidate(roleId, rootId, groupId) {
    let payload = void 0;
    if (typeof roleId !== 'undefined') {
      payload = {role_id: roleId};
    }

    if (typeof rootId !== 'undefined') {
      payload = {root_id: rootId};
    }

    if (typeof groupId !== 'undefined') {
      payload = {manage_group_id: groupId};
    }

    return ApiService.request('user_validate', payload, apiFormatters.BASIC, false, cacheNamespaces.AUTH_API);
  }

  /**
   * Calls User/Logout to end user's session.
   *
   * @return {Promise}
   */
  static logout() {
    apiRequestInstance.purgeCache();
    try {
      ComponentsApiService.clearMasteryDemoCache();
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
    return ApiService.request('user_logout', void 0, apiFormatters.BASIC, false, cacheNamespaces.AUTH_API);
  }

  static reportSaveSearch(searchReport) {
    return ApiService.request('report_save_search', searchReport, apiFormatters.BASIC, false);
  }
  /**
   *
   * @param {Number} id
   * @return {Promise}
   */
  static userGet(id, type) {
    return ApiService.request('user_get', {id, type}, apiFormatters.BASIC, false, cacheNamespaces.USER_API);
  }

  /**
   *
   * @param {Array} id
   * @return {Promise}
   */
  static userProfileDelete(id) {
    return ApiService.request('user_profile_delete', {id}, apiFormatters.BASIC, true, cacheNamespaces.USER_API);
  }
  /**
   * @todo missing phone
   *
   * @param {Object} arguments
   * @param {String} arguments[].firstName
   * @param {String} arguments[].lastName
   * @param {String} arguments[].email
   * @param {String} arguments[].password
   * @param {String} arguments[].phone
   * @param {String} arguments[].status
   * @param {String} arguments[].type
   * @param {Number} arguments[].groupId
   * @param {Object|Array} arguments[].roles
   * @param {Object|Array} arguments[].newRoles
   *
   * @return {Promise}
   */
  static userCreate({externalId, firstName, lastName, email, password, phone, status, type, groupId, roles, newRoles}) {
    const payload = {
      external_id: externalId,
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      status,
      type,
      group_id: groupId,
      roles,
      phone,
      new_roles: newRoles
    };

    apiRequestInstance.getCacheManager(cacheNamespaces.USER_API).clear();
    return ApiService.request('user_create', payload, apiFormatters.BASIC, false);
  }
  /**
   * @todo missing phone, password
   *
   * @param {Object} arguments
   * @param {Number} arguments[].id
   * @param {String} arguments[].firstName
   * @param {String} arguments[].lastName
   * @param {String} arguments[].email
   * @param {String} arguments[].phone
   * @param {Object|Array} arguments[].roles
   *
   * @return {Promise}
   */
  static userUpdate({
    id,
    externalId,
    firstName,
    lastName,
    email,
    phone,
    roles,
    newRoles,
    contactEmail,
    contactPhone,
    mfaEnabled,
    mfaMethod,
    profileId
  }) {
    const payload = {
      id,
      external_id: externalId,
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      roles,
      new_roles: newRoles,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      mfa_enabled: mfaEnabled === 'on',
      mfa_method: mfaMethod,
      profile_id: profileId,
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.USER_API).clear();

    return ApiService.request('user_update', payload, apiFormatters.BASIC, false);
  }

  /**
   *
   * @param {Object} arguments
   * @param {Number} arguments[].id
   * @param {String} arguments[].email
   * @param {String} arguments[].phone
   * @param {String} arguments[].password
   * @param {String} arguments[].mfa_method
   *
   * @return {Promise}
   */
  // eslint-disable-next-line camelcase
  static userUpdateLogin({id, email, phone, password, mfa_method}) {
    const payload = {
      id,
      email,
      phone,
      password,
      mfa_method
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.USER_API).clear();

    return ApiService.request('user_update_login', payload, apiFormatters.BASIC, false);
  }

  static adminSearch(search, filters, sort) {
    const payload = {search, sort};
    if (Object.keys(filters).length) {
      payload.filters = filters;
    }
    return ApiService.request('user_search', payload, apiFormatters.BASIC, false, cacheNamespaces.USER_API);
  }

  /**
   *
   * @param {Number} userId
   * @param {Number} groupId
   */
  static roleDelete(userId, groupId) {
    const payload = {user_id: userId, group_id: groupId};
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    apiRequestInstance.getCacheManager(cacheNamespaces.USER_API).clear();

    return ApiService.request('role_delete', payload, apiFormatters.BASIC, false);
  }

  /**
   * @param {Number} profileId
   * @param {Array} newRoles
   * @param {Array} deletedRoleIds
   * @returns {Promise<void>}
   */
  static userUpdateRoles(profileId, newRoles, deletedRoleIds) {
    const payload = {
      profile_id: profileId,
      new_roles: newRoles,
      deleted_role_ids: deletedRoleIds,
    };

    return ApiService.request('user_update_roles', payload, apiFormatters.BASIC, false);
  }

  static managementGroupUpdate({
    id,
    name,
    parentId,
    externalId,
    phone,
    country,
    address,
    address2,
    state,
    city,
    zip,
    topLevel,
    dashboardGroupIds,
  }) {
    const payload = {
      id,
      name,
      parent_id: parentId,
      external_id: externalId,
      country_code: country,
      address_line_1: address,
      address_line_2: address2,
      city,
      state,
      zip,
      phone,
      top_level: topLevel,
      dashboard_group_ids: dashboardGroupIds
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    return ApiService.request(
      'management_group_update',
      payload,
      apiFormatters.BASIC,
      false,
    );
  }

  /**
   * Get management group info
   *
   * @param {*} id
   * @return {Promise}
   */
  static managementGroupGet(id) {
    const payload = {id};
    return ApiService.request(
      'management_group_get',
      payload,
      apiFormatters.BASIC,
      true,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static managementGroupCreate({
    name,
    parentId,
    externalId,
    phone,
    country,
    address,
    address2,
    state,
    city,
    zip,
    dashboardGroupIds,
  }) {
    const payload = {
      name,
      parent_id: parentId,
      external_id: externalId,
      country_code: country,
      address_line_1: address,
      address_line_2: address2,
      city,
      state,
      zip,
      phone,
      dashboard_group_ids: dashboardGroupIds
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    return ApiService.request(
      'management_group_create',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  /**
   * Calls ManagementGroupSearch endpoint
   *
   * @static
   * @param {Object} search
   * @param {String} search[].query
   * @param {String} search[].field [id, external_id, name]
   *
   * @param {Object} filters
   * @param {String} filters.path
   * @param {String} filters.level
   * @param {String} filters.parent_group_id
   *
   * @param {Object} sort
   * @param {String} sort.direction [asc, desc]
   * @param {String} sort.fields [id, name, external_id, parent_group_name]
   * @param {String} sort.last_values
   * @param {String} sort.is_forward
   *
   * @return {Promise}
   */
  static managementGroupSearch(search, filters, sort, fromCache = false) {
    const payload = {search, filters, sort};
    return ApiService.request(
      'management_group_search',
      payload,
      apiFormatters.BASIC,
      fromCache,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  /**
   * @param id
   * @returns {Promise}
   */
  static groupDelete(id = []) {
    const payload = {id};
    apiRequestInstance.purgeCache();
    return ApiService.request(
      'group_delete',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  /**
   * @param id
   * @returns {Promise}
   */
  static groupArchive(id = []) {
    const payload = {id};
    apiRequestInstance.purgeCache();
    return ApiService.request(
      'group_archive',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  /**
   * @param id
   * @returns {Promise}
   */
  static classArchive(id = []) {
    const payload = {id};
    apiRequestInstance.purgeCache();
    return ApiService.request(
      'classroom_group_archive',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  /**
   * @param id
   * @returns {Promise}
   */
  static classDelete(id = []) {
    const payload = {id};
    apiRequestInstance.purgeCache();
    return ApiService.request(
      'classroom_group_delete',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static classRestore(payload) {
    apiRequestInstance.purgeCache();
    return ApiService.request(
      'classroom_group_restore',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API);
  }

  static groupRestore(payload) {
    apiRequestInstance.purgeCache();
    return ApiService.request(
      'group_restore',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  /**
   * Calls ManagementGroup/ListByParent endpoint
   *
   * @static
   * @param {Number} id id of group
   * @return {Promise}
   */
  static groupListByParent(id) {
    const payload = {id};
    return ApiService.request(
      'group_list_by_parent',
      payload,
      apiFormatters.BASIC,
      true,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  /**
   * Calls Group/ListNestedByParent endpoint
   *
   * @static
   * @param {Number} id id of group
   * @param {Boolean} showThirdPartyConnected - filter to determine if
   * all groups should be shown, or if 3rd party groups should be filtered out
   * @return {Promise}
   */
  static groupListNestedByParent(id, showThirdPartyConnected) {
    const payload = {id, show_third_party_connected: showThirdPartyConnected};
    return ApiService.request(
      'group_list_nested_by_parent_path',
      payload,
      apiFormatters.BASIC,
      true,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }
  /**
   * Calls Student/Search endpoint
   *
   * @static
   * @param {Object} search
   * @param {String} search[].query
   * @param {String} search[].field [id, external_id, name]
   *
   * @param {Object} filters
   * @param {String} filters.path
   * @param {String} filters.level
   * @param {String} filters.parent_group_id
   *
   * @param {Object} sort
   * @param {String} sort.direction [asc, desc]
   * @param {String} sort.fields [id, name, external_id, parent_group_name]
   * @param {String} sort.last_values
   * @param {String} sort.is_forward
   *
   * @return {Promise}
   */
  static studentSearch(search, filters, sort) {
    const payload = {search, filters, sort};
    return ApiService.request('student_search', payload, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static studentCreate(payload) { // eslint-disable-line
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    apiRequestInstance.getCacheManager(cacheNamespaces.AUTH_API).clear();

    return ApiService.request('student_create', payload, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static studentUpdate(payload) { // eslint-disable-line
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    return ApiService.request('student_update', payload, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static studentRestore(payload) {
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    return ApiService.request('student_restore', payload, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static studentArchive(payload) {
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    return ApiService.request('student_archive', payload, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static studentUpdateTpLanguage(id, language) { // eslint-disable-line
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    return ApiService.request('student_update_language', {id, language}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static studentGet(id) {
    return ApiService.request('student_get', {id}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }
  static studentGetDetails(id) {
    return ApiService.request('student_get_details', {id}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }
  static studentGetByIds(ids) {
    return ApiService.request('student_get_by_ids', {ids}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static studentRemove(student_ids) { // eslint-disable-line
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    window.Appcues && window.Appcues.track('bulkActionStudentRemove');
    return ApiService.request('student_remove', {student_ids}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }
  /**
   * Calls Teacher/Search endpoint
   *
   * @static
   * @param {Object} search
   * @param {String} search[].query
   * @param {String} search[].field [id, external_id, name]
   *
   * @param {Object} filters
   * @param {String} filters.path
   * @param {String} filters.level
   * @param {String} filters.parent_group_id
   *
   * @param {Object} sort
   * @param {String} sort.direction [asc, desc]
   * @param {String} sort.fields [id, name, external_id, parent_group_name]
   * @param {String} sort.last_values
   * @param {String} sort.is_forward
   *
   * @return {Promise}
   */
  static teacherSearch(search, filters, sort) {
    const payload = {search, filters, sort};
    return ApiService.request('teacher_search', payload, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static teacherCreate(firstName, lastName, externalId, email, phone, preferredCommunication, classIds) {
    const payload = {
      first_name: firstName,
      last_name: lastName,
      external_id: externalId,
      email,
      phone,
      preferred_communication: preferredCommunication,
      class_ids: classIds,
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    return ApiService.request('teacher_create', payload, apiFormatters.BASIC, false);
  }

  // eslint-disable-next-line max-len
  static teacherUpdate(id, firstName, lastName, externalId, contactEmail, contactPhone, preferredCommunication, classIds, profileId, hasUniqueProfile) {
    const payload = {
      id,
      first_name: firstName,
      last_name: lastName,
      external_id: externalId,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      preferred_communication: preferredCommunication,
      class_ids: classIds,
      profile_id: profileId,
      has_unique_profile: hasUniqueProfile,
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    return ApiService.request('teacher_update', payload, apiFormatters.BASIC, false);
  }

  static teacherDelete(id) {
    return ApiService.request('teacher_delete', {profile_id: id}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static teacherArchive(id) {
    return ApiService.request('teacher_archive', {id}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static teacherRestore(payload) {
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    return ApiService.request('teacher_restore', payload, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static teacherUpdatePreferredCommunication(id, preferredCommunication) {
    const payload = {
      id,
      preferred_communication: preferredCommunication,
    };
    return ApiService.request('teacher_update_preferred_communication', payload,
      apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static teacherGet(id) {
    return ApiService.request('teacher_get', {profile_id: id}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static teacherHasUniqueProfile(id, contactEmail, contactPhone, profileId, originalRootId) {
    const payload = {
      id,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      profile_id: profileId,
      original_root_id: originalRootId
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    return ApiService.request('teacher_has_unique_profile', payload, apiFormatters.BASIC, false);
  }

  static classroomGroupSearch(search, filters, sort, fromCache = false) {
    const payload = {search, filters, sort};
    return ApiService.request(
      'classroom_group_search',
      payload,
      apiFormatters.BASIC,
      fromCache,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static classroomGroupGet(id) {
    const payload = {id};
    return ApiService.request(
      'classroom_group_get',
      payload,
      apiFormatters.BASIC,
      true,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static classroomGroupCreate(name, parentId, externalId, teacherIds, studentIds, dashboardGroupsIds) {
    const payload = {
      name,
      parent_id: parentId,
      external_id: externalId,
      teacher_ids: teacherIds,
      student_ids: studentIds,
      dashboard_group_ids: dashboardGroupsIds
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    return ApiService.request(
      'classroom_group_create',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }
  static classroomGroupUpdate(id, name, parentId, externalId, teacherIds, studentIds, dashboardGroupsIds) {
    const payload = {
      id,
      name,
      parent_id: parentId,
      external_id: externalId,
      teacher_ids: teacherIds,
      student_ids: studentIds,
      dashboard_group_ids: dashboardGroupsIds
    };
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();

    return ApiService.request(
      'classroom_group_update',
      payload,
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static classroomGroupGetGrades(classroomid, product) {
    const payload = {
      classroom_id: classroomid,
      product,
    };
    return ApiService.request(
      'classroom_group_get_grades',
      payload,
      apiFormatters.BASIC,
      true,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static exportSearch(args) {
    const payload = {
      search_type: args.searchType,
      search_params: {
        search: args.search,
        filters: args.filters,
        sort: args.sort
      },
      fields: args.fields,
      filter_ids: args.filterIds,
      not_selected: args.notSelected,
      selection_mode: args.selectionMode,
      i18n: args.i18n,
    };

    window.Appcues && window.Appcues.track('bulkActionExportSearch');
    return ApiService.request('export_search_create', payload, apiFormatters.BASIC, false);
  }

  static generateQr(payload) {
    // payload = { 'students': [1, 2, 3], 'teachers': [1, 2, 3], 'classes': [1, 2, 3] }
    return ApiService.request('generate_qr_create', payload, apiFormatters.BASIC, false);
  }

  static userJobGet(jobId) {
    const payload = {user_job_id: jobId};
    return ApiService.request('user_job_get', payload, apiFormatters.BASIC, false);
  }

  static userJobGetList(filteredJobs = []) {
    const payload = {filtered_jobs: filteredJobs};
    return ApiService.request('user_job_get_list', payload, apiFormatters.BASIC, false);
  }

  static userJobGetCancel(userJobId) {
    const payload = {user_job_id: userJobId};
    return ApiService.request('user_job_cancel', payload, apiFormatters.BASIC, false);
  }

  static rosterJobCreateAofl({file, groupId, rosterType, fileType = 'aofl'}) {
    const payload = {
      arguments: {
        file_field: 'file',
        group_id: groupId,
        file_type: fileType,
        roster_type: rosterType,
        language: localStorage.getItem(localStorageKeys.LANG)
      },
      file
    };
    return ApiService.request('roster_job_create_aofl', payload, apiFormatters.UPLOAD, false);
  }

  static rosterJobCreateOneRoster({file, groupId, rosterType, fileType = 'oneroster'}) {
    const payload = {
      arguments: {
        file_field: 'file',
        group_id: groupId,
        file_type: fileType,
        roster_type: rosterType,
        language: localStorage.getItem(localStorageKeys.LANG)
      },
      file
    };
    return ApiService.request('roster_job_create_oneroster', payload, apiFormatters.UPLOAD, false);
  }

  static rosterJobCommit(jobId, options) {
    const payload = {
      user_job_id: jobId,
      options: options || {}
    };

    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    apiRequestInstance.getCacheManager(cacheNamespaces.AUTH_API).clear();

    return ApiService.request('roster_job_commit', payload, apiFormatters.BASIC, false);
  }

  static rosterJobGetListPromise;

  static rosterJobGetList() {
    if (!this.rosterJobGetListPromise) {
      this.rosterJobGetListPromise = ApiService.request('roster_job_get_list', void 0, apiFormatters.BASIC, false)
        .then((resp) => {
          this.rosterJobGetListPromise = null;
          return resp;
        });
    }

    return this.rosterJobGetListPromise;
  }

  static rosterExportJob() {
    return ApiService.request('roster_job_export', {language: localStorage.getItem(localStorageKeys.LANG)}, apiFormatters.BASIC, false);
  }

  static rosterExport(format, onlyHeaders) {
    const args = {
      format,
      only_headers: onlyHeaders,
      language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
    };
    return ApiService.request('roster_export', args, apiFormatters.BASIC, false);
  }

  static statsTeacherDashboard(groupid, product, start, end) {
    return ApiService.request(
      'stats_teacher_dashboard',
      {
        groupid,
        product,
        start,
        end,
        test_id: this.tallyTestId,
        language: localStorage.getItem(localStorageKeys.LANG)
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }
  static dashboardGetGroupHistoricUsage(groupId, product, startEnd) {
    return ApiService.request(
      'dashboard_get_group_historic_usage',
      {
        group_id: groupId,
        product,
        start_end: startEnd
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API
    );
  }

  static statsStudentDashboard(product, studentId) {
    return ApiService.request(
      'stats_student_dashboard',
      {
        product,
        student_id: studentId,
        test_id: this.tallyTestId,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }

  static statsSummary(payload) {
    return ApiService.request('stats_summary', payload, apiFormatters.BASIC, true, cacheNamespaces.MANUAL_ROSTER_API);
  }
  static statsOverallProgress(groupId, product, searchParams, externalStandardSet) {
    return ApiService.request(
      'stats_overall_progress',
      {
        group_id: groupId,
        product,
        search: searchParams?.search,
        sort: searchParams?.sort,
        test_id: this.tallyTestId,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
        external_standard_id: externalStandardSet
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }

  static getClassroomOverallProgress(groupId, product, externalStandardSet) {
    return ApiService.request(
      'dashboard_get_classroom_overall_progress',
      {
        group_id: groupId,
        product,
        external_standard_id: externalStandardSet
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }

  static studentGetOverallProgress(studentId, product, externalStandardId) {
    return ApiService.request(
      'student_get_overall_progress',
      {
        student_id: studentId,
        product,
        test_id: this.tallyTestId,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
        external_standard_id: externalStandardId
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }

  static helpResourceProductHasReports(product) {
    return ApiService.request('help_resource_get_has_reports', {product}, apiFormatters.BASIC, true, cacheNamespaces.HELP_RESOURCE);
  }

  static helpResourceGetNextDropdown(payload) {
    return ApiService.request('help_resource_get_next_report_dropdown', payload, apiFormatters.BASIC, true, cacheNamespaces.HELP_RESOURCE);
  }

  static helpResourceGetNextDropdownInfo(payload) {
    return ApiService.request('help_resource_get_next_report_dropdown_info', payload, apiFormatters.BASIC, true, cacheNamespaces.HELP_RESOURCE);
  }

  static helpResourceDownloadReport(payload) {
    return ApiService.request('help_resource_download_get_report_key', payload, apiFormatters.BASIC, true, cacheNamespaces.HELP_RESOURCE);
  }

  static licenseGetInventory(groupId) {
    let payload = void 0;
    if (typeof groupId !== 'undefined') {
      payload = {group_id: groupId};
    }
    return ApiService.request('license_get_inventory', payload, apiFormatters.BASIC, true, cacheNamespaces.LICENSE_API);
  }

  static licenseSearch(sort, filters) {
    const payload = {};
    if (typeof sort !== 'undefined' && Object.keys(sort).length) {
      payload.sort = sort;

      if (typeof payload.sort.fields === 'undefined') {
        payload.sort.fields = ['id', 'name'];
      }
      if (typeof payload.sort.direction === 'undefined') {
        payload.sort.direction = 'desc';
      }
      if (typeof payload.sort.is_forward === 'undefined') {
        payload.sort.is_forward = true;
      }
    }
    if (typeof filters !== 'undefined' && Object.keys(filters).length) {
      payload.filters = filters;
    }
    return ApiService.request('license_search', payload, apiFormatters.BASIC, false, cacheNamespaces.LICENSE_API);
  }

  static licenseSearchAssigned(args) {
    return ApiService.request('license_search_assigned', args, apiFormatters.BASIC, false, cacheNamespaces.LICENSE_API);
  }

  static licenseGetDetails(licenseId) {
    return ApiService.request('license_get_details', {license_id: licenseId}, apiFormatters.BASIC, true, cacheNamespaces.LICENSE_API);
  }
  static groupLicenseGetGroupAndChildLicenseCounts(licenseId, groupId) {
    return ApiService.request('group_license_get_group_and_child_license_counts', {license_id: licenseId, group_id: groupId}, apiFormatters.BASIC, true, cacheNamespaces.LICENSE_API);
  }

  static searchLicenseAllocations(search, filters, sort, fromCache = true) {
    const payload = {search, filters, sort};
    return ApiService.request('group_search_license_allocations', payload, apiFormatters.BASIC, fromCache, cacheNamespaces.LICENSE_API);
  }
  static emailResubscribe(args) {
    return ApiService.request('email_resubscribe', args, apiFormatters.BASIC, false);
  }
  static emailUnsubscribe(args) {
    return ApiService.request('email_unsubscribe', args, apiFormatters.BASIC, false);
  }

  static licenseDistribute({groupId, quantity, licenseId, transferType = licenseTransferTypes.transferTo}) {
    apiRequestInstance.getCacheManager(cacheNamespaces.LICENSE_API).clear();

    window.Appcues && window.Appcues.track('bulkActionLicenseDistribute');
    return ApiService.request(
      'license_transfer_inventory',
      {license_id: licenseId, group_id: groupId, quantity, transfer_type: transferType},
      apiFormatters.BASIC,
      false
    );
  }

  static licenseGetInvoice(args) {
    return ApiService.request('license_job_get_invoice', args, apiFormatters.BASIC, true, cacheNamespaces.LICENSE_API);
  }

  static licenseAssign(args) {
    apiRequestInstance.getCacheManager(cacheNamespaces.LICENSE_API).clear();

    window.Appcues && window.Appcues.track('bulkActionlicenseAssign');
    return ApiService.request('license_job_create', args, apiFormatters.BASIC, false);
  }

  static licenseDeactivate({studentId, licenseId, transferStudentId}) {
    const args = {
      student_id: studentId,
      license_id: licenseId
    };
    if (transferStudentId) args['transfer_student_id'] = transferStudentId; // eslint-disable-line
    return ApiService.request('license_deactivate', args, apiFormatters.BASIC, false);
  }

  static licenseBulkRevoke(args) {
    window.Appcues && window.Appcues.track('bulkActionLicenseDeactivate');
    return ApiService.request('license_job_deactivate', args, apiFormatters.BASIC, false);
  }

  static getUnredeemedCountsByProduct(args) {
    return ApiService.request('get_unredeemed_counts_by_product', args, apiFormatters.BASIC, false);
  }

  static sendInvites(args) {
    window.Appcues && window.Appcues.track('bulkActionSendInvites');
    return ApiService.request('invitation_job_create', args, apiFormatters.BASIC, false);
  }

  static sendPlatformInvites(args) {
    return ApiService.request('invitation_job_create_platform', args, apiFormatters.BASIC, false);
  }

  static rosterJobConnectThirdParty(args) {
    apiRequestInstance.getCacheManager(cacheNamespaces.AUTH_API).clear();

    return ApiService.request('roster_job_connect_third_party', args, apiFormatters.BASIC, false);
  }

  static rosterJobCreateThirdParty(args) {
    apiRequestInstance.getCacheManager(cacheNamespaces.MANUAL_ROSTER_API).clear();
    apiRequestInstance.getCacheManager(cacheNamespaces.AUTH_API).clear();

    return ApiService.request('roster_job_create_third_party', args, apiFormatters.BASIC, false);
  }

  static rosterJobDisconnectThirdParty(args) {
    apiRequestInstance.getCacheManager(cacheNamespaces.AUTH_API).clear();

    return ApiService.request('roster_job_disconnect_third_party', args, apiFormatters.BASIC, false);
  }

  static requestPasswordReset(args) {
    return ApiService.request('user_request_password_reset', args, apiFormatters.BASIC, false);
  }

  static userVerifyPasswordToken(args) {
    return ApiService.request('user_verify_password_token', args, apiFormatters.BASIC, false);
  }

  static userResetPassword(args) {
    return ApiService.request('user_reset_password', args, apiFormatters.BASIC, false);
  }

  static abcmouseGetPasscodeInfo() {
    return ApiService.request('abcmouse_get_passcode_info', {}, apiFormatters.BASIC, cacheNamespaces.LICENSE_API);
  }

  /**
   * @deprecated Use productUpdatePasscode
   */
  static abcmouseUpdatePasscode(args) {
    return ApiService.request('abcmouse_update_passcode', args, apiFormatters.BASIC, false);
  }

  static productUpdatePasscode(args) {
    return ApiService.request('product_update_passcode', args, apiFormatters.BASIC, false);
  }

  static bulkPictureResetPasscode(args) {
    return ApiService.request('bulk_update_picture_passcode', args, apiFormatters.BASIC, false);
  }

  static userActivateProducts(args) {
    apiRequestInstance.getCacheManager(cacheNamespaces.AUTH_API).clear();
    return ApiService.request('user_activate_products', args, apiFormatters.BASIC, false);
  }

  static classlinkFinishSetup() {
    apiRequestInstance.getCacheManager(cacheNamespaces.AUTH_API).clear();
    return ApiService.request('classlink_finish_setup', {}, apiFormatters.BASIC, false);
  }

  static licenseGetInventoryFluctuation(args) {
    return ApiService.request('license_get_inventory_fluctuation', args, apiFormatters.BASIC, false);
  }

  static inventoryUpdatesLogSearch(args) {
    return ApiService.request('inventory_update_log_search', args, apiFormatters.BASIC, false);
  }

  static allocationLogSearch(args) {
    return ApiService.request('allocation_log_search', args, apiFormatters.BASIC, false, cacheNamespaces.LICENSE_API);
  }

  static studentGetTimeSpentPerSubject(product, studentId, startTimestamp, endTimestamp, period = 'week') {
    return ApiService.request(
      'student_get_time_spent_per_subject',
      {student_id: studentId, product, start_timestamp: startTimestamp, end_timestamp: endTimestamp, period, test_id: this.tallyTestId},
      apiFormatters.BASIC,
      true,
      cacheNamespaces.USER_API);
  }

  static studentGetActivitiesCompletedPerSubject(product, studentId, startTimestamp, endTimestamp, period = 'week') {
    return ApiService.request('student_get_activities_completed_per_subject',
      {student_id: studentId, product, start_timestamp: startTimestamp, end_timestamp: endTimestamp, period, test_id: this.tallyTestId},
      apiFormatters.BASIC,
      true,
      cacheNamespaces.USER_API);
  }

  static studentDelete(id = []) { // eslint-disable-line
    return ApiService.request('student_delete', {id}, apiFormatters.BASIC, false, cacheNamespaces.MANUAL_ROSTER_API);
  }

  static tallyGet(args, normalizeData = true, fromCache = false) {
    return ApiService.request(
      'tally_get',
      {requests: args, normalize_data: normalizeData},
      apiFormatters.BASIC,
      fromCache);
  }

  static studentGetTimeSpent(args, fromCache = true) {
    return ApiService.request(
      'student_get_time_spent',
      {
        ...args,
        test_id: this.tallyTestId
      },
      apiFormatters.BASIC,
      fromCache);
  }

  static getManagementGroupTotal(search, filters, exists_only = true) {
    return ApiService.request(
      'management_group_total',
      {search, filters, exists_only},
      apiFormatters.BASIC,
      exists_only,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static getClassroomGroupTotal(search, filters, exists_only = true) {
    return ApiService.request(
      'classroom_group_total',
      {search, filters, exists_only},
      apiFormatters.BASIC,
      exists_only,
      cacheNamespaces.MANUAL_ROSTER_API);
  }

  static getTeacherTotal(search, filters, exists_only = true) {
    return ApiService.request(
      'teacher_total',
      {search, filters, exists_only},
      apiFormatters.BASIC,
      exists_only,
      cacheNamespaces.MANUAL_ROSTER_API);
  }

  static getStudentTotal(search, filters, exists_only = true) {
    return ApiService.request(
      'student_total',
      {search, filters, exists_only},
      apiFormatters.BASIC,
      exists_only,
      cacheNamespaces.MANUAL_ROSTER_API);
  }

  static licenseCheckCompatibility(args) {
    return ApiService.request('license_check_compatibility', args, apiFormatters.BASIC, false);
  }

  static statsGetConceptsMastered(groupId, product) {
    return ApiService.request(
      'stats_get_concepts_mastered',
      {
        group_id: groupId,
        product,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API);
  }

  static masterySharedGetSkillInfo(id, product) {
    return ApiService.request(
      'mastery_shared_get_skill_info',
      {
        id,
        product,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
      },
      apiFormatters.BASIC,
      true
    );
  }

  static recommendedActivityIsDownloadable(ids) {
    return ApiService.request(
      'recommended_activity_check_is_downloadable',
      {
        id: ids,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
      },
      apiFormatters.BASIC,
      true
    );
  }

  static skillsGetOneOneOneInfo(skillId, product) {
    return ApiService.request(
      'skills_get_one_one_info',
      {
        skill_id: skillId,
        product,
        language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
      },
      apiFormatters.BASIC,
      true
    );
  }

  static userVerifyActivationCode(code) {
    return ApiService.request('user_verify_activation_code', {code}, apiFormatters.BASIC, false);
  }

  static userRedeem(args) {
    return ApiService.request('user_redeem', args, apiFormatters.BASIC, false);
  }

  static parentAddChild(id, code) {
    return ApiService.request(
      'parent_add_child',
      {id, code},
      apiFormatters.BASIC,
      false
    );
  }

  static parentCreate(code, firstName, email, password) {
    return ApiService.request(
      'parent_create',
      {
        code,
        firstName,
        email,
        password,
      },
      apiFormatters.BASIC,
      false
    );
  }

  static parentUpdateInfo(args) {
    return ApiService.request('parent_update_info', args, apiFormatters.BASIC, false);
  }

  static parentUpdateLoginEmail(args) {
    return ApiService.request('parent_update_login_email', args, apiFormatters.BASIC, false);
  }

  static caregiverSkipActivation(args) {
    return ApiService.request('caregiver_skip_activation', args, apiFormatters.BASIC, false);
  }

  static groupGet(ids, fields, isDashboardGroup = false) {
    return ApiService.request(
      'group_get',
      {
        ids,
        fields,
        is_dashboard_group: isDashboardGroup,
      },
      apiFormatters.BASIC,
      true
    );
  }

  static groupSearch(search, filters, sort, showThirdPartyConnected) {
    return ApiService.request(
      'group_search',
      {
        search,
        filters,
        sort,
        show_third_party_connected: showThirdPartyConnected,
        include: {summary: false},
      },
      apiFormatters.BASIC,
      true
    );
  }

  static userAcknowledgeLicenseConflicts(timestamp) {
    return ApiService.request('user_acknowledge_license_conflicts', {timestamp}, apiFormatters.BASIC, false);
  }

  static userAcknowledgeNoMoreLicenseAvailable(timestamp) {
    return ApiService.request('user_acknowledge_no_more_license_available', {timestamp}, apiFormatters.BASIC, false);
  }

  static groupGetUnassignedStudentCount(groupPath, product, startTimestamp, endTimestamp) {
    return ApiService.request('group_get_unassigned_student_count', {
      group_path: groupPath,
      product,
      range: {start: startTimestamp, end: endTimestamp},
    }, apiFormatters.BASIC, true);
  }

  static clearNamespace(namespace) {
    apiRequestInstance.clearCache(namespace);
  }

  static activityMetadataGetKnowledgeComponent(ids, product) {
    return ApiService.request('activity_metadata_get_knowledge_component', {
      ids,
      product
    }, apiFormatters.BASIC, true, cacheNamespaces.DASHBOARD_STATS_API);
  }

  static dashboardGetStudentStats(studentId, product) {
    return ApiService.request('dashboard_get_student_stats', {
      student_id: studentId,
      product,
      language: localStorage.getItem(localStorageKeys.LANG)
    }, apiFormatters.BASIC, true, cacheNamespaces.DASHBOARD_STATS_API);
  }

  static dashboardGetStudentStatsV2(studentId, studentUUID, product) {
    return ApiService.request('dashboard_get_student_stats_v2', {
      student_id: studentId,
      student_uuid: studentUUID,
      product,
      language: localStorage.getItem(localStorageKeys.LANG)
    }, apiFormatters.BASIC, true, cacheNamespaces.DASHBOARD_STATS_API);
  }

  static dashboardGetStudentTimeSpent(args) {
    const {studentId, product, start, end, aggregate} = args;
    return ApiService.request('dashboard_get_student_time_spent', {
      student_id: studentId,
      product,
      start,
      end,
      aggregate
    }, apiFormatters.BASIC, true);
  }

  static dashboardGetStudentTimeSpentHomeAndSchool(args) {
    const {studentId, product, startTimestamp, endTimestamp, aggregate} = args;
    return ApiService.request(
      'dashboard_get_student_time_spent_home_and_school',
      {
        student_id: studentId,
        product,
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
        aggregate,
      },
      apiFormatters.BASIC,
      true,
      cacheNamespaces.DASHBOARD_STATS_API
    );
  }

  static dashboardGetCompletedSkills(args) {
    const {studentId, product, startTimestamp, endTimestamp, aggregate} = args;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ApiService.request('dashboard_get_student_completed_skills', {
      student_id: studentId,
      product,
      start_timestamp: startTimestamp,
      end_timestamp: endTimestamp,
      aggregate,
      timezone,
    }, apiFormatters.BASIC, true);
  }

  static dashboardGetUpdateTimestamps(product) {
    return ApiService.request('dashboard_get_update_timestamps', {product}, apiFormatters.BASIC, false);
  }

  static dashboardGetStudentProgress(studentId, product, externalStandardSetId) {
    return ApiService.request('dashboard_get_student_progress', {
      student_id: studentId,
      product,
      external_standard_set_id: externalStandardSetId,
      language: localStorage.getItem(localStorageKeys.LANG)
    }, apiFormatters.BASIC);
  }

  static parentGetStudents() {
    return ApiService.request('parent_get_students', {}, apiFormatters.BASIC, true);
  }

  static parentGetStudentStats(studentId, product) {
    const payload = {
      student_id: studentId,
      product,
      language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
    };
    return ApiService.request('parent_get_student_stats', payload, apiFormatters.BASIC, true);
  }
  static parentGetSkill(skillId, product) {
    const payload = {
      skill_id: skillId,
      language: localStorage.getItem(localStorageKeys.LANG),
      product,
    };
    return ApiService.request('parent_get_skill', payload, apiFormatters.BASIC, true);
  }

  static dashboardGroupGetGroupsByIds(dashboardGroupIds) {
    const payload = {
      dashboard_group_ids: dashboardGroupIds,
    };
    return ApiService.request('dashboard_group_get_groups_by_ids', payload, apiFormatters.BASIC, true);
  }

  static updateDashboardMemberGroups(id, dashboardGroupIds, type) {
    return ApiService.request(
      'dashboard_group_update_member_dashboard_groups',
      {
        id,
        dashboard_group_ids: dashboardGroupIds,
        type,
      },
      apiFormatters.BASIC,
      false,
      cacheNamespaces.MANUAL_ROSTER_API
    );
  }

  static sftpConnect() {
    return ApiService.request('sftp_connect', {}, apiFormatters.BASIC, false);
  }

  static sftpDisconnect() {
    return ApiService.request('sftp_disconnect', {}, apiFormatters.BASIC, false);
  }

  static sftpSettings(payload) {
    return ApiService.request('sftp_settings', payload, apiFormatters.BASIC, true);
  }

  static studentUpdateDemographics(id, demographics) {
    const overrides = demographics.overrides;
    delete demographics.overrides;

    return ApiService.request(
      'student_update_demographics',
      {
        student_id: id,
        demographics,
        overrides,
      },
      apiFormatters.BASIC,
      false
    );
  }

  static classroomGroupGetStudents(product, groupId) {
    const payload = {
      product,
      group_id: groupId,
      initials_only: false
    };
    return ApiService.request('classroom_group_get_students', payload, apiFormatters.BASIC, false);
  }

  static skillsSearch(filters = null, search = null, include = null, sort = null) {
    return ApiService.request('skills_search', {
      filters,
      search,
      include,
      sort,
      language: localStorage.getItem(localStorageKeys.LANG) || defaultLocale,
    }, apiFormatters.BASIC, true);
  }

  static async getAction(key) {
    await resourceEnumerateInstance.ready;
    const reData = await resourceEnumerateInstance.get(apiNamespaces.EMS);
    return reData.payload.action[key];
  }

  static getGenerateStudentQRLogin(queryParams = "") {
    let host = ComponentsApiService.getSageHostname();
    return host + '/pages/product/qrprint/students?' + queryParams.toString();
  }

  static getGenerateUserQRLogin(queryParams = "") {
    let host = ComponentsApiService.getSageHostname();
    return host + '/pages/product/qrprint/teachers?' + queryParams.toString();
  }

  static getGenerateGroupQRLogin(queryParams = "") {
    let host = ComponentsApiService.getSageHostname();
    return host + '/pages/product/qrprint/classrooms?' + queryParams.toString();
  }

  /**
   * @param {boolean} enabled
   * @returns {Promise<void>}
   */
  static groupSettingsConfigureLearningAssistant(enabled) {
    return ApiService.request(
      'group_settings_configure_learning_assistant',
      {
        enabled,
      },
      apiFormatters.BASIC,
      false,
    );
  }
}

export {
  ApiService,
  apiRequestInstance,
};
